import posthog from "posthog-js";
import { ContentType } from "@/shared/gql/graphql";
import type {
  AnalyticsEventArgs,
  AnalyticsEventName,
  ContentSection,
  TrackedContentType,
} from "./events";

/**
 * Tracking is internal and best-effort: users must never see it or be affected
 * by it. Every export in this folder runs its whole body, including the work of
 * deriving properties, through `silently`, so a missing field or a PostHog
 * failure is swallowed here instead of breaking a render, a route or a mutation.
 */
export const silently = (run: () => void): void => {
  try {
    run();
  } catch {
    // Intentionally swallowed: see above.
  }
};

/** False until PostHog has initialised, so every call is a no-op before that. */
export const isAnalyticsReady = (): boolean => posthog.__loaded === true;

/**
 * The only way named events are emitted. The event name PostHog receives is the
 * full `section.component.action` string; the three segments are also copied
 * onto the event as properties, because PostHog can group by a property but
 * cannot split an event name.
 */
export const track = <Event extends AnalyticsEventName>(
  event: Event,
  ...args: AnalyticsEventArgs<Event>
): void =>
  silently(() => {
    if (!isAnalyticsReady()) {
      // Without a key there is no PostHog; a local run still shows what fires.
      if (import.meta.env.DEV)
        console.debug("[analytics]", event, args[0] ?? {});
      return;
    }

    const [section, component, action] = event.split(".");

    posthog.capture(event, {
      ...args[0],
      section,
      component,
      action,
    });
  });

/**
 * Turns an enum value the app already uses into the stable code we send:
 * `TwoYears` becomes `two_years`, `Start` becomes `start`. The same value
 * always produces the same code, whatever language the UI is in. Callers run
 * it outside `silently`, so anything but a string gives undefined.
 */
export const toTrackingCode = (value?: string | null): string | undefined =>
  typeof value === "string" && value
    ? value.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase()
    : undefined;

const SECTIONS: Partial<Record<ContentType, ContentSection>> = {
  [ContentType.Tender]: "tenders",
  [ContentType.Organisation]: "organisations",
  [ContentType.CaseLaw]: "caselaw",
  [ContentType.ProcurementCalendar]: "horizon",
};

/**
 * The section a list event belongs to. Admin content types map to nothing:
 * the admin panel is excluded from analytics, so callers skip the event.
 */
export const contentSection = (
  contentType?: ContentType | null,
): ContentSection | undefined =>
  contentType ? SECTIONS[contentType] : undefined;

const CONTENT_TYPES: Partial<Record<ContentType, TrackedContentType>> = {
  [ContentType.Tender]: "tender",
  [ContentType.Organisation]: "organisation",
  [ContentType.CaseLaw]: "caselaw",
  [ContentType.ProcurementCalendar]: "horizon_item",
};

/** The `content_type` property value for a content type. */
export const trackedContentType = (
  contentType?: ContentType | null,
): TrackedContentType | undefined =>
  contentType ? CONTENT_TYPES[contentType] : undefined;
